import type { Id } from '../types';
import { keySimilarity, stem, tokens } from './similarity';

export interface ProductRef {
  productId: Id;
  name: string;
  normKey: string;
}

/** Decyzja użytkownika: czy pozycja o danym kluczu to ten sam produkt. */
export interface ProductLink {
  normKey: string;
  productId: Id;
  verdict: 'same' | 'different';
}

export interface MatchResult {
  exact: Id | null;
  /** Posortowane malejąco po score, maks. 3. */
  candidates: { productId: Id; score: number }[];
}

/** Źródło podpowiedzi dopasowań (lokalne teraz, AI później). */
export interface MatchProvider {
  suggest(key: string, products: ProductRef[]): Promise<{ productId: Id; confidence: number }[]>;
}

const MAX_CANDIDATES = 3;
const MIN_SIMILARITY = 0.6;
const SUBSET_SCORE = 0.8;
const SHARED_HEAD_SCORE = 0.65;

/** `key` to wynik normKey(). Dokładne dopasowanie łączymy automatycznie, kandydaci wymagają potwierdzenia. */
export function findMatches(key: string, products: ProductRef[], links: ProductLink[]): MatchResult {
  if (key.trim() === '') return { exact: null, candidates: [] };

  const keyLinks = links.filter((l) => l.normKey === key);
  const confirmed = keyLinks.find((l) => l.verdict === 'same');
  if (confirmed) return { exact: confirmed.productId, candidates: [] };
  const rejected = new Set(keyLinks.filter((l) => l.verdict === 'different').map((l) => l.productId));

  const usable = products.filter((p) => p.normKey.trim() !== '' && !rejected.has(p.productId));
  const identical = usable
    .filter((p) => p.normKey === key)
    .map((p) => p.productId)
    .sort(compareIds)[0];
  if (identical !== undefined) return { exact: identical, candidates: [] };

  const keyTokens = new Set(tokens(key));
  const keyStems = tokens(key).map(stem);
  const scores = new Map<Id, number>();
  for (const p of usable) {
    const productTokens = new Set(tokens(p.normKey));
    const subset = isSubset(keyTokens, productTokens) || isSubset(productTokens, keyTokens);
    const floor = subset
      ? SUBSET_SCORE
      : sharesHead(keyStems, tokens(p.normKey).map(stem))
        ? SHARED_HEAD_SCORE
        : null;
    const sim = keySimilarity(key, p.normKey);
    if (floor === null && sim < MIN_SIMILARITY) continue;
    const score = Math.max(floor ?? 0, sim);
    scores.set(p.productId, Math.max(score, scores.get(p.productId) ?? 0));
  }

  const candidates = [...scores]
    .map(([productId, score]) => ({ productId, score }))
    .sort((a, b) => b.score - a.score || compareIds(a.productId, b.productId))
    .slice(0, MAX_CANDIDATES);
  return { exact: null, candidates };
}

export const localMatchProvider: MatchProvider = {
  suggest(key, products) {
    const { exact, candidates } = findMatches(key, products, []);
    return Promise.resolve(
      exact !== null
        ? [{ productId: exact, confidence: 1 }]
        : candidates.map((c) => ({ productId: c.productId, confidence: c.score })),
    );
  },
};

/** „Banany bio zielone” ~ „Banany bio mniej dojrzałe”: ten sam pierwszy rdzeń i min. połowa rdzeni krótszego klucza w dłuższym. */
function sharesHead(a: readonly string[], b: readonly string[]): boolean {
  if (a[0] === undefined || a[0] !== b[0]) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  const inLonger = new Set(longer);
  return shorter.filter((s) => inLonger.has(s)).length * 2 >= shorter.length;
}

function isSubset<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function compareIds(a: Id, b: Id): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
